import http from './http';

export interface CreditCardInfoRec {
  id: string;
  userId: string;
  cardIssuer?: string;
  cardType?: string;
  cardNumber?: string; // Stored in vault
  expirationDate?: string;
  cvv?: string; // Stored in vault
  billingAddress?: string;
  customerServicePhone?: string;
  onlineAccountUrl?: string;
  creditLimit?: number;
  autopayEnabled?: boolean;
  autopayBankAccount?: string;
  notes?: string;
  isLocked?: boolean;
}

class CreditCardInfoService {
  async getAllCreditCardInfo(targetUserId?: string): Promise<CreditCardInfoRec[]> {
    const url = targetUserId
      ? `/api/credit-card-info?targetUserId=${targetUserId}`
      : '/api/credit-card-info';

    const response = await http.get(url);

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to fetch credit card information');
    }

    return response.data.data || [];
  }

  async getCreditCardInfo(targetUserId?: string): Promise<CreditCardInfoRec[]> {
    return this.getAllCreditCardInfo(targetUserId);
  }

  async createCreditCardInfo(data: Partial<CreditCardInfoRec>): Promise<CreditCardInfoRec> {
    const response = await http.post('/api/credit-card-info', data);
    
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to save credit card information');
    }

    return response.data.data;
  }

  async updateCreditCardInfo(id: string, data: Partial<CreditCardInfoRec>): Promise<CreditCardInfoRec> {
    const response = await http.patch(`/api/credit-card-info/${id}`, data);
    
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to update credit card information');
    }

    return response.data.data;
  }

  async deleteCreditCardInfo(id: string): Promise<void> {
    const response = await http.delete(`/api/credit-card-info/${id}`);
    
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to delete credit card information');
    }
  }

  // Lock/Unlock methods
  async lockCreditCardInfo(id: string): Promise<void> {
    const response = await http.post(`/api/credit-card-info/${id}/lock`);
    
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to lock credit card information');
    }
  }

  async unlockCreditCardInfo(id: string, granteeEmails: string[]): Promise<void> {
    const response = await http.post(`/api/credit-card-info/${id}/unlock`, {
      granteeEmails
    });
    
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to unlock credit card information');
    }
  }

  async getCreditCardAccessGrants(id: string): Promise<any[]> {
    const response = await http.get(`/api/credit-card-info/${id}/access-grants`);
    
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to get access grants');
    }

    return response.data.data || [];
  }
}

export const creditCardInfoService = new CreditCardInfoService();